// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders  (types, API and formatters for pages/Orders.tsx and pages/OrderDetail.tsx)
// The signed-in customer's orders (GET/POST /api/me/orders → backend `customerOrders`). An order exists only for a
// payment the backend has VERIFIED, and its status is advanced by ZenFresh staff; every change is timestamped in
// `history`, so the timeline only shows times for steps that really happened. Times are shown in India time.
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";

export type OrderStatus = "placed" | "confirmed" | "preparing" | "out_for_delivery" | "delivered" | "cancelled";

export interface OrderLine {
  id: string;
  name: string;
  pack: "500g" | "1kg";
  cut: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
}

export interface ItemReview {
  id: string;
  rating: number;
  cleaning: "excellent" | "good" | "needs_work" | null;
  cutAsOrdered: boolean | null;
}

export interface Review {
  overall: number;
  praise: string[];
  items: ItemReview[];
  delivery: { rating: number; tags: string[] } | null;
  comment: string | null;
  createdAt: string;
}

/** What goes to the server: only what the customer actually filled in. */
export interface ReviewInput {
  overall: number;
  praise: string[];
  items: { id: string; rating: number; cleaning?: ItemReview["cleaning"]; cutAsOrdered?: boolean }[];
  delivery?: { rating: number; tags: string[] };
  comment?: string;
}

export interface Order {
  ref: string;
  /** Why a cancelled order was cancelled (only when a reason was asked for). */
  cancellation?: { code: string; label: string; by: "customer" | "staff"; note: string | null } | null;
  status: OrderStatus;
  /** false once delivered or cancelled (the "Past orders" tab). */
  active: boolean;
  /** true only while ZenFresh hasn't confirmed the order yet. */
  canCancel: boolean;
  /** ZF-INV-<year>-<6 digits>, assigned when the order was created. */
  invoiceNumber: string | null;
  /** Set once a cancelled order's money is on its way back (or couldn't be started). */
  /** The card/UPI part of a cancelled order: sent to Razorpay ("initiated"), credited to the wallet, or "failed" (staff finish it). */
  refund: { amountPaise: number; status: "initiated" | "credited" | "failed"; at: string; refundId: string | null; destination: "original" | "wallet" } | null;
  /** Rupees of the total that were paid from the wallet, and how many of those were credited back on cancellation. */
  walletAmount: number;
  walletRefunded: number;
  /** The customer's review of the delivered order, once given (one per order). */
  review: Review | null;
  history: { status: OrderStatus; at: string }[];
  items: OrderLine[];
  subtotal: number;
  deliveryFee: number;
  discount: number;
  couponCode: string | null;
  total: number;
  /** null when the wallet paid the whole order. */
  payment: { razorpayOrderId: string; paymentId: string; amountPaise: number; method: string | null } | null;
  delivery: { name: string; phone: string; addressLine: string; city: string; pincode: string; notes: string[] };
  createdAt: string;
}

export interface CreateOrderInput {
  orderRef: string;
  /** Both absent when the wallet pays the whole order. */
  razorpayOrderId?: string;
  paymentId?: string;
  items: { id: string; name: string; pack: string; cut: string; qty: number; unitPrice: number; lineTotal: number }[];
  subtotal: number;
  deliveryFee: number;
  discount: number;
  couponCode: string | null;
  total: number;
  /** Rupees of `total` paid from the wallet. */
  walletAmount: number;
  delivery: Order["delivery"];
}

export const fetchOrders = () => apiAuthed<Order[]>("GET", "/me/orders");
export const fetchOrder = (ref: string) => apiAuthed<Order>("GET", `/me/orders/${encodeURIComponent(ref)}`);
/** `refundTo` chooses where the card/UPI part goes: back to the original payment method, or to the wallet (instant). */
export const cancelOrder = (ref: string, refundTo: "original" | "wallet" = "original", reasonCode?: string, note?: string) =>
  apiAuthed<Order>("POST", `/me/orders/${encodeURIComponent(ref)}/cancel`, { refundTo, ...(reasonCode ? { reasonCode } : {}), ...(note ? { note } : {}) });
export const submitReview = (ref: string, input: ReviewInput) => apiAuthed<Order>("POST", `/me/orders/${encodeURIComponent(ref)}/review`, input);
export const createOrder = (input: CreateOrderInput) => apiAuthed<Order>("POST", "/me/orders", input);

/** The forward path an order takes, with the customer-facing wording for each step. */
export const STEPS: { status: Exclude<OrderStatus, "cancelled">; title: string; detail: string }[] = [
  { status: "placed", title: "Payment received & order placed", detail: "Your payment was confirmed and the order is with ZenFresh." },
  { status: "confirmed", title: "Order confirmed", detail: "ZenFresh has confirmed your order." },
  { status: "preparing", title: "Being prepared", detail: "Your catch is being cut, cleaned and packed as you chose." },
  { status: "out_for_delivery", title: "Out for delivery", detail: "Your order has left for your address." },
  { status: "delivered", title: "Delivered", detail: "Your order has been delivered." },
];

export const STATUS_LABEL: Record<OrderStatus, string> = {
  placed: "Awaiting confirmation",
  confirmed: "Confirmed",
  preparing: "Being prepared",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const IST = "Asia/Kolkata";

/** "Wed, 23 Oct • 07:14 AM" in IST. */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: IST }).format(d);
  const time = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: IST })
    .format(d)
    .replace(/\s?([ap])m$/i, (_, l: string) => ` ${l.toUpperCase()}M`);
  return `${day} • ${time}`;
}

/** When the order reached a status, if it did. */
export const reachedAt = (order: Order, status: OrderStatus): string | undefined => order.history.find((h) => h.status === status)?.at;

/** "2 items" — counts packs, since that is what the customer ordered. */
export const packCount = (order: Order): number => order.items.reduce((sum, line) => sum + line.qty, 0);

/** "via UPI", "from wallet", "via UPI + wallet", or just "online" when Razorpay couldn't say how. */
export function paidVia(order: Order): string {
  const labels: Record<string, string> = { upi: "UPI", card: "card", netbanking: "net banking", wallet: "wallet", emi: "EMI", paylater: "pay later" };
  if (!order.payment) return "from wallet";
  const label = order.payment.method ? labels[order.payment.method] : undefined;
  const card = label ? `via ${label}` : "online";
  return order.walletAmount > 0 ? `${card} + wallet` : card;
}

/** "just now", "6 mins ago", "3 hours ago", else the date. */
export function timeAgo(iso: string, now = Date.now()): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  return formatWhen(iso);
}

/** The line shown under an order's title: when it was delivered / cancelled, else when it was placed. */
export function headline(order: Order): string {
  const delivered = reachedAt(order, "delivered");
  if (delivered) return `Delivered ${formatWhen(delivered)}`;
  const cancelled = reachedAt(order, "cancelled");
  if (cancelled) return `Cancelled ${formatWhen(cancelled)}`;
  return `Placed ${timeAgo(order.createdAt)}`;
}

/** The ticks a customer can add to their stars (ids must match backend/app/domain/order.py). */
export const PRAISE_TAGS: { id: string; label: string }[] = [
  { id: "fresh", label: "Fresh" },
  { id: "well-cleaned", label: "Well cleaned" },
  { id: "well-cut", label: "Cut just right" },
  { id: "good-packaging", label: "Good packaging" },
  { id: "right-weight", label: "Right weight" },
  { id: "good-value", label: "Good value" },
];
export const DELIVERY_TAGS: { id: string; label: string; positive: boolean }[] = [
  { id: "on-time", label: "On time", positive: true },
  { id: "polite", label: "Polite & courteous", positive: true },
  { id: "careful-handling", label: "Handled with care", positive: true },
  { id: "packaging-intact", label: "Packaging intact", positive: true },
  { id: "late", label: "Late", positive: false },
  { id: "packaging-damaged", label: "Packaging damaged", positive: false },
];
export const MOODS = ["Needs improvement", "Fair", "Good", "Very fresh & clean", "Excellent & ocean fresh!"];
